import React, { useCallback, useContext, useState } from 'react'
import moment from 'moment'
import { SEVEN_DAYS_AFTER_PURCHASE, TWENTY_ONE_DAYS_BEFORE_CHECK_IN } from 'constants/cancellationPolicies'
import BodyText from 'components/Luxkit/Typography/BodyText'
import HelpPane from 'components/Common/HelpPane'
import Modal from 'components/Luxkit/Modal/Modal'
import LineRefundIcon from 'components/Luxkit/Icons/line/LineRefundIcon'
import TextLink from 'components/Luxkit/TextLink'
import { INTL_DATE_TIME_FORMAT, INTL_DAY_MONTH_NAME_SHORT } from 'constants/dateFormats'
import Clickable from 'components/Common/Clickable'
import SolidInfoCircleIcon from 'components/Luxkit/Icons/solid/SolidInfoCircleIcon'
import styled from 'styled-components'
import BookingProtectionModal from 'checkout/Components/CheckoutSteps/Review/BookingProtection/BookingProtectionModal'
import ModalContext from 'contexts/ModalContext'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'

const MiddledTooltipIcon = styled(SolidInfoCircleIcon)`
  vertical-align: middle;
`

interface Props {
  propertyTimezoneOffset?: number;
  checkInDate?: string | moment.Moment;
  isFlashUSP?: boolean;
  isBookingProtectionEnabled?: boolean;
}

function OfferPropertyCancellationPolicies(props: Props) {
  const { propertyTimezoneOffset, checkInDate, isFlashUSP, isBookingProtectionEnabled = false } = props
  const [modalOpen, setModalOpen] = useState(false)
  const intlDateFormatter = useIntlDateFormatter()

  const startBasedOnToday = propertyTimezoneOffset ? moment().utcOffset(propertyTimezoneOffset).add(SEVEN_DAYS_AFTER_PURCHASE, 'days') : moment().add(SEVEN_DAYS_AFTER_PURCHASE, 'days')
  const startBasedOnCheckIn = checkInDate && (propertyTimezoneOffset ? moment(checkInDate).utcOffset(propertyTimezoneOffset).subtract(TWENTY_ONE_DAYS_BEFORE_CHECK_IN, 'days') : moment(checkInDate).subtract(TWENTY_ONE_DAYS_BEFORE_CHECK_IN, 'days'))
  let startDate: moment.Moment | undefined
  if (startBasedOnCheckIn) {
    startDate = moment.min(startBasedOnToday, startBasedOnCheckIn)
  }

  const bookingProtectionLastEligibleRefundDate = checkInDate ? moment(checkInDate).add(60, 'days') : undefined

  const showModal = useContext(ModalContext)

  const onItemClick = useCallback(() => {
    if (isBookingProtectionEnabled && startDate) {
      showModal(<BookingProtectionModal cancellationType="free" cancelUntilFullRefundDate={startDate} bookingProtectionLastEligibleRefundDate={bookingProtectionLastEligibleRefundDate} />)
    } else {
      setModalOpen(true)
    }
  }, [isBookingProtectionEnabled, showModal, startDate, bookingProtectionLastEligibleRefundDate])

  const onClose = useCallback(() => {
    setModalOpen(false)
  }, [])

  if (startDate?.isBefore(moment())) {
    return null
  }

  return <>
    {!isFlashUSP && <Clickable onClick={onItemClick}>
      <BodyText variant="medium" weight="bold" as="span" colour="highlight-secondary">Free cancellation</BodyText>{' '}
      {!startDate && <BodyText variant="medium" as="span">within 7 days of purchase (hotel only)</BodyText>}
      {startDate && <BodyText variant="medium" as="span">until {intlDateFormatter(startDate, INTL_DAY_MONTH_NAME_SHORT)} (hotel only)</BodyText>}
      {' '}<MiddledTooltipIcon size="XS" />
    </Clickable>}
    {isFlashUSP && <TextLink
      size="medium"
      variant="highlight"
      underline={false}
      data-type="7day"
      onClick={onItemClick}
      startIcon={<LineRefundIcon />}
    >
      Free cancellation
    </TextLink>
}
    <Modal
      title="Free cancellation"
      isOpen={modalOpen}
      onClose={onClose}
    >
      {!checkInDate && <BodyText variant="medium" as="p">
        Things don’t always work out. Our 7-day ‘change of mind’ guarantee is there to help.{' '}
        Limited Time Lux Exclusive bookings (excluding flights) may be cancelled with a full refund{' '}
        provided that cancellation occurs strictly within 7 days from the date of purchase{' '}
        and provided that the cancellation is made no less than 14 days prior to the check-in date.{' '}
        Please note: flights and experiences are subject to the refund policies of the respective operator.
      </BodyText>}
      {checkInDate && startDate && <BodyText variant="medium" as="p">
        Standard bookings can be cancelled with a full refund provided the cancellation is made by {intlDateFormatter(startDate, INTL_DATE_TIME_FORMAT)} (local time of property).
      </BodyText>}
      <HelpPane />
    </Modal>
  </>
}

export default OfferPropertyCancellationPolicies
